import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getApiError } from '../../utils/errorHelper';
import { StatusBadge } from './ui/DesignSystem';
import {
  Loader2, ArrowLeft, Eye, Download, Upload, Check, XCircle,
  FileText, Building2, User, CheckCircle, Clock, AlertCircle,
  AlertTriangle, Send, MessageSquare, Activity, Shield, CreditCard,
  Mail, X, FormInput, ChevronRight, ExternalLink, Plus, RotateCw, Percent, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { toIST } from '../../utils/dateUtils';
import api, { adminApi, ordersApi, dscConsentApi } from '../../services/api';
import GSTFilingTab from './GSTFilingTab';
import ClientViewModal from './ClientViewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getFileUrl = (fileUrl) => {
  const token = localStorage.getItem('token');
  if (!fileUrl) return '';
  return `${API_URL}${fileUrl}${fileUrl.includes('?') ? '&' : '?'}token=${token}`;
};
const FILING_FREQUENCIES = ['monthly', 'quarterly', 'yearly', 'custom'];

const STEPS = [
  { key: 'draft', label: 'Draft' }, { key: 'invoice_requested', label: 'Invoice' },
  { key: 'payment_pending', label: 'Payment' }, { key: 'payment_received', label: 'Paid' },
  { key: 'documents_pending', label: 'Documents' }, { key: 'under_review', label: 'Review' },
  { key: 'signature_pending', label: 'Signature' }, { key: 'signature_review', label: 'Sign Review' },
  { key: 'completed', label: 'Complete' },
];

const GOVT_CFG = {
  not_started:            { label: 'Not Started',            cls: 'draft' },
  submitted_to_govt:      { label: 'Submitted to Govt',      cls: 'progress' },
  pending_approval:       { label: 'Pending Approval',       cls: 'pending' },
  clarification_required: { label: 'Clarification Required', cls: 'due' },
  clarification_filed:    { label: 'Clarification Filed',    cls: 'review' },
  approved:               { label: 'Approved',               cls: 'complete' },
  rejected:               { label: 'Rejected',               cls: 'cancelled' },
};

// ── Stepper ───────────────────────────────────────────────
const Stepper = ({ currentStatus, isFiling }) => {
  const steps = isFiling ? STEPS.filter(s => s.key !== 'signature_pending' && s.key !== 'signature_review') : STEPS;
  const cancelled = currentStatus === 'cancelled';
  const idx = steps.findIndex(s => s.key === currentStatus);
  return (
    <div className="admin-card" style={{ padding: '16px 20px', overflowX: 'auto', marginBottom: 20 }} data-testid="progress-stepper">
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 'max-content' }}>
        {steps.map((s, i) => {
          const done = !cancelled && idx > i;
          const active = !cancelled && idx === i;
          const bg = cancelled ? 'var(--admin-red-soft)' : done ? 'var(--admin-green)' : active ? 'var(--admin-blue)' : 'var(--admin-bg-hover)';
          const fg = done || active ? '#fff' : 'var(--admin-text-soft)';
          const line = done ? 'var(--admin-green)' : 'var(--admin-border)';
          const lblColor = cancelled ? 'var(--admin-red)' : done ? 'var(--admin-green)' : active ? 'var(--admin-blue)' : 'var(--admin-text-soft)';
          return (
            <React.Fragment key={s.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {done ? <Check style={{ width: 14, height: 14, color: fg }} /> : <span style={{ fontSize: 10, fontWeight: 700, color: fg }}>{i + 1}</span>}
                </div>
                <span style={{ fontSize: 10, marginTop: 4, color: lblColor, fontWeight: active ? 700 : 500 }}>{s.label}</span>
              </div>
              {i < steps.length - 1 && <div style={{ flex: 1, height: 2, minWidth: 20, margin: '0 4px', background: line, borderRadius: 1, alignSelf: 'flex-start', marginTop: 12 }} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// ── Deadline Card ─────────────────────────────────────────
const DeadlineCard = ({ label, date, icon: Icon }) => {
  if (!date) return null;
  const d = new Date(date);
  const days = Math.ceil((d - new Date()) / 86400000);
  const overdue = days < 0;
  const urgent = days >= 0 && days <= 3;
  const accent = overdue || urgent ? 'var(--admin-red)' : days <= 7 ? 'var(--admin-amber)' : 'var(--admin-green)';
  return (
    <div className="admin-card" style={{ padding: 16, borderLeft: `3px solid ${accent}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon style={{ width: 14, height: 14, color: accent }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--admin-text-dim)' }}>{label}</span>
        {(overdue || urgent) && <AlertTriangle style={{ width: 12, height: 12, color: 'var(--admin-red)' }} />}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent, fontFamily: "'Lora', serif" }}>
        {d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
      </div>
      <div style={{ fontSize: 11, color: overdue ? 'var(--admin-red)' : 'var(--admin-text-soft)', marginTop: 2 }}>
        {overdue ? `Overdue by ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''}` : `${days} day${days !== 1 ? 's' : ''} remaining`}
      </div>
    </div>
  );
};

// ── Govt Filing Section ───────────────────────────────────
const GovtSection = ({ order, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(order?.govt_filing_status || 'not_started');
  const [remark, setRemark] = useState(order?.govt_filing_remark || '');
  const [appNum, setAppNum] = useState(order?.govt_application_number || '');
  const [saving, setSaving] = useState(false);
  const cur = order?.govt_filing_status || 'not_started';
  const cfg = GOVT_CFG[cur] || GOVT_CFG.not_started;

  return (
    <div className="admin-card" style={{ padding: 20 }} data-testid="govt-filing-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Building2 style={{ width: 16, height: 16, color: 'var(--admin-blue)' }} /> Government Filing
        </h3>
        {!editing && <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setEditing(true)} data-testid="govt-filing-edit-btn">Update</button>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ padding: 12, background: 'var(--admin-bg)', borderRadius: 8, border: '1px solid var(--admin-border-soft)' }}>
          <div className="admin-input-label">Status</div>
          <span className={`admin-status ${cfg.cls}`} style={{ marginTop: 4 }}><span className="admin-status-dot" />{cfg.label}</span>
        </div>
        <div style={{ padding: 12, background: 'var(--admin-bg)', borderRadius: 8, border: '1px solid var(--admin-border-soft)' }}>
          <div className="admin-input-label">Application Number</div>
          <div style={{ fontFamily: "'SF Mono', monospace", fontSize: 14, color: 'var(--admin-text)' }}>{order?.govt_application_number || '\u2014'}</div>
        </div>
      </div>
      {editing && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--admin-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div className="admin-input-label">Status</div>
            <select className="admin-select" value={status} onChange={e => setStatus(e.target.value)} data-testid="govt-status-select">
              {Object.entries(GOVT_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <div className="admin-input-label">Application Number</div>
            <input className="admin-input" value={appNum} onChange={e => setAppNum(e.target.value)} placeholder="SRN-123456" data-testid="govt-app-number-input" />
          </div>
          <div>
            <div className="admin-input-label">
              Remark {status === 'rejected' && <span style={{ color: 'var(--admin-red)' }}>* (mandatory, min 10 characters)</span>}
            </div>
            <textarea
              className="admin-input"
              rows={3}
              value={remark}
              onChange={e => setRemark(e.target.value)}
              placeholder={status === 'rejected'
                ? 'Explain why this order is being rejected — client will see this verbatim'
                : 'Enter remarks...'}
              data-testid="govt-remark-input"
            />
            {status === 'rejected' && (
              <div style={{ fontSize: 11, color: remark.trim().length >= 10 ? 'var(--admin-text-soft)' : 'var(--admin-red)', marginTop: 4 }} data-testid="govt-remark-helper">
                {remark.trim().length}/10 characters minimum
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="admin-btn admin-btn-primary admin-btn-sm"
              disabled={saving || (status === 'rejected' && remark.trim().length < 10)}
              data-testid="govt-save-btn"
              onClick={async () => {
                if (status === 'rejected' && remark.trim().length < 10) {
                  toast.error('A remark of at least 10 characters is required when rejecting an order.');
                  return;
                }
                setSaving(true);
                try { await onUpdate(status, remark, appNum); setEditing(false); } catch {} finally { setSaving(false); }
              }}>
              {saving ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Check style={{ width: 14, height: 14 }} />} Save
            </button>
            <button className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => { setEditing(false); setStatus(cur); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const OrderDetailView = ({
  orderDetail, onBack, onReviewSubmission, onUpdateStatus,
  onUploadAck, uploadingAck, ackDocName, setAckDocName, ackFileRef, ackFileName, setAckFileName,
  onCompleteOrder, onSendInvoice, onMarkPaymentReceived, onActivateDocuments, onMockPayment,
  reviewingSubmission, setReviewingSubmission, reviewComment, setReviewComment,
  signatureCaption, setSignatureCaption, signatureFileRef, signatureFileName, setSignatureFileName,
  onUploadSignatureDocument, uploadingSignature, onReviewSignedDocument, onDeleteSignatureDocument, onDeleteAcknowledgement,
  onRefreshOrder, onNavigateToCompany, onNavigateToUser, currentUser
}) => {
  const { order, company, user, product, submissions, requirements, signature_documents, acknowledgement_documents } = orderDetail;
  const isFiling = FILING_FREQUENCIES.includes(product?.frequency_settings?.frequency);

  // ── State ─────────────────────────────────────────────
  const [showMockDialog, setShowMockDialog] = useState(false);
  const [mockRemark, setMockRemark] = useState('');
  const [kycGapData, setKycGapData] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [remarkPopup, setRemarkPopup] = useState({ show: false, remark: '', docName: '' });
  const [signedRejectModal, setSignedRejectModal] = useState({ show: false, doc: null, remark: '' });
  const [signedActionLoading, setSignedActionLoading] = useState({});
  const [rejectionHistoryPopup, setRejectionHistoryPopup] = useState({ show: false, history: [], docName: '' });
  const [deleteSigConfirm, setDeleteSigConfirm] = useState({ show: false, doc: null });
  const [deleteAckConfirm, setDeleteAckConfirm] = useState({ show: false, doc: null });
  const [ackDeleting, setAckDeleting] = useState(false);
  const [showClientView, setShowClientView] = useState(false);
  const [dscConsents, setDscConsents] = useState([]);
  const [sendingDsc, setSendingDsc] = useState(false);
  const [showDscModal, setShowDscModal] = useState(false);
  const [detailTab, setDetailTab] = useState('notes');
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [noteContent, setNoteContent] = useState('');
  const [noteFile, setNoteFile] = useState(null);
  const [noteFileName, setNoteFileName] = useState('');
  const [postingNote, setPostingNote] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [syncingAsana, setSyncingAsana] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null); // { id, short_url, status, amount }
  const [linkLoading, setLinkLoading] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountVal, setDiscountVal] = useState('20');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const noteFileRef = useRef(null);

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const handleAddToAsana = async () => {
    if (!order?.id || syncingAsana) return;
    setSyncingAsana(true);
    try {
      await adminApi.syncOrderToAsana(order.id);
      toast.success('Added to Asana successfully');
      if (onRefreshOrder) await onRefreshOrder();
    } catch (e) {
      toast.error(getApiError(e, 'Failed to add to Asana'));
    } finally {
      setSyncingAsana(false);
    }
  };

  const handleGeneratePaymentLink = async () => {
    if (!order?.id || linkLoading) return;
    setLinkLoading(true);
    try {
      const res = await adminApi.createPaymentLink(order.id);
      setPaymentLink(res);
      setShowLinkModal(true);
      if (res.status === 'already_paid') toast.info('This order is already paid.');
      else toast.success('Payment link ready — copy and share');
    } catch (e) {
      toast.error(getApiError(e, 'Failed to generate payment link'));
    } finally { setLinkLoading(false); }
  };

  const loadPaymentLink = useCallback(async (opts = {}) => {
    if (!order?.id) return null;
    try {
      const res = await adminApi.getPaymentLink(order.id);
      if (res.has_link) setPaymentLink(res);
      // If Razorpay reports paid but our order data hasn't refreshed, refresh order
      if (res.status === 'paid' && order?.payment_status !== 'paid' && onRefreshOrder) {
        await onRefreshOrder();
        if (opts.notify) toast.success('Payment received! Order marked as paid.');
      }
      return res;
    } catch (e) { return null; }
  }, [order?.id, order?.payment_status, onRefreshOrder]);

  useEffect(() => { loadPaymentLink(); }, [loadPaymentLink]);

  // Auto-poll every 5s while modal is open and link is unpaid
  useEffect(() => {
    if (!showLinkModal || !paymentLink) return undefined;
    if (paymentLink.status !== 'created' && paymentLink.status !== 'issued' && paymentLink.status !== 'partially_paid') return undefined;
    const timer = setInterval(() => { loadPaymentLink({ notify: true }); }, 5000);
    return () => clearInterval(timer);
  }, [showLinkModal, paymentLink, loadPaymentLink]);

  const refreshPaymentStatus = async () => {
    setLinkLoading(true);
    try {
      const res = await loadPaymentLink({ notify: true });
      if (res && res.status === 'paid') {
        toast.success('Payment confirmed via Razorpay');
      } else if (res) {
        toast.info(`Status: ${res.status}`);
      }
    } finally { setLinkLoading(false); }
  };

  const copyPaymentLink = async () => {
    if (!paymentLink?.short_url) return;
    try {
      await navigator.clipboard.writeText(paymentLink.short_url);
      toast.success('Payment link copied to clipboard');
    } catch {
      toast.error('Copy failed — please copy manually');
    }
  };

  const applyDiscount = async () => {
    const pct = parseFloat(discountVal);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error('Discount must be 0–100'); return; }
    setApplyingDiscount(true);
    try {
      const res = await adminApi.applyDiscount(order.id, pct);
      toast.success(`Discount ${pct}% applied · New total ₹${res.total_amount.toLocaleString('en-IN')}`);
      setShowDiscount(false);
      setPaymentLink(null); // link was invalidated on backend
      if (onRefreshOrder) await onRefreshOrder();
    } catch (e) {
      toast.error(getApiError(e, 'Failed to apply discount'));
    } finally { setApplyingDiscount(false); }
  };

  // ── Loaders ───────────────────────────────────────────
  const refreshKyc = useCallback(async () => { if (!order?.id) return; setKycLoading(true); try { setKycGapData(await ordersApi.getKycGap(order.id)); } catch { setKycGapData(null); } finally { setKycLoading(false); } }, [order?.id]);
  const loadDsc = useCallback(async () => { if (!order?.id) return; try { setDscConsents(await dscConsentApi.getHistory(order.id)); } catch { setDscConsents([]); } }, [order?.id]);
  const loadNotes = useCallback(async () => { setLoadingNotes(true); try { setNotes(await adminApi.getOrderNotes(order.id)); } catch { setNotes([]); } finally { setLoadingNotes(false); } }, [order?.id]);
  const loadActivity = useCallback(async () => { setLoadingActivity(true); try { setActivity(await adminApi.getOrderActivity(order.id)); } catch { setActivity([]); } finally { setLoadingActivity(false); } }, [order?.id]);

  useEffect(() => { refreshKyc(); }, [refreshKyc]);
  useEffect(() => { loadDsc(); }, [loadDsc]);
  useEffect(() => { if (detailTab === 'notes') loadNotes(); if (detailTab === 'activity') loadActivity(); }, [detailTab, loadNotes, loadActivity]);

  const handlePostNote = async () => {
    if (!noteContent.trim()) return;
    setPostingNote(true);
    try { await adminApi.createOrderNote(order.id, noteContent.trim(), noteFile); setNoteContent(''); setNoteFile(null); setNoteFileName(''); toast.success('Note posted'); await loadNotes(); } catch { toast.error('Failed to post note'); } finally { setPostingNote(false); }
  };

  const handleSendDsc = async () => {
    setSendingDsc(true);
    try { await dscConsentApi.sendRequest(order.id, product?.name || 'Filing'); toast.success('DSC consent request sent'); setShowDscModal(false); loadDsc(); if (onRefreshOrder) await onRefreshOrder(); } catch (e) { toast.error(getApiError(e, 'Failed to send DSC request')); } finally { setSendingDsc(false); }
  };

  const kycDocs = kycGapData?.documents || [];
  const summary = kycGapData?.summary || {};
  const hasKyc = kycGapData?.kyc_required;
  const showInvoice = ['invoice_requested', 'payment_pending', 'payment_received'].includes(order?.status);
  const showMock = order?.status === 'draft' && user?.role === 'super_admin';

  const allStatuses = ['invoice_requested', 'payment_pending', 'payment_received', 'documents_pending', 'under_review', 'signature_pending', 'signature_review'];
  const filteredStatuses = isFiling ? allStatuses.filter(s => s !== 'signature_pending' && s !== 'signature_review') : allStatuses;

  // ── Payment Gate ──────────────────────────────────────
  const POST_PAYMENT_STATUSES = ['payment_received', 'documents_pending', 'under_review', 'signature_pending', 'signature_review', 'completed'];
  const PAID_STATES = ['paid', 'mock_paid', 'prepaid', 'confirmed'];
  const isPaid = PAID_STATES.includes(order?.payment_status);
  const isBypassed = !!order?.payment_bypassed_by_super_admin;
  const paymentGateOpen = isPaid || isBypassed;
  // Regular admins are blocked from advancing to post-payment stages if unpaid.
  const canAdvancePastPayment = paymentGateOpen || isSuperAdmin;

  const guardedUpdateStatus = (s) => {
    if (POST_PAYMENT_STATUSES.includes(s) && !canAdvancePastPayment) {
      toast.error('Payment not received. Only Super Admin can advance this order past payment.');
      return;
    }
    onUpdateStatus(s);
  };
  const guardedComplete = () => {
    if (!canAdvancePastPayment) {
      toast.error('Payment not received. Only Super Admin can complete this order.');
      return;
    }
    onCompleteOrder();
  };

  const SUB_CFG = { submitted: { label: 'Pending Review', cls: 'pending' }, approved: { label: 'Approved', cls: 'complete' }, rejected: { label: 'Rejected', cls: 'cancelled' } };

  // ── Render ────────────────────────────────────────────
  return (
    <div data-testid="order-detail-view">
      {/* Back */}
      <button onClick={onBack} className="admin-btn admin-btn-ghost" style={{ marginBottom: 16 }} data-testid="order-detail-back-btn">
        <ArrowLeft style={{ width: 16, height: 16 }} /> Back to Orders
      </button>

      {/* Header */}
      <div className="admin-card" style={{ padding: 24, marginBottom: 20 }} data-testid="order-header-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: "'SF Mono', monospace", color: 'var(--admin-text-soft)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>Order #{order?.id?.slice(0, 8)}</div>
            <h1 style={{ fontFamily: "'Lora', serif", fontSize: 28, fontWeight: 700, letterSpacing: '-0.01em', margin: '0 0 8px' }}>{product?.name}</h1>
            {!isBypassed && order?.payment_status === 'paid' && (
              <div data-testid="paid-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.35)', color: '#34D399', fontSize: 12, fontWeight: 500, marginBottom: 8 }}>
                <CheckCircle style={{ width: 12, height: 12 }} />
                Paid via {order.payment_mode === 'razorpay_link' ? 'Razorpay Link' : 'Razorpay'}
                {order.payment_date && ` on ${toIST(order.payment_date, 'dd MMM yyyy')}`}
                {order.razorpay_payment_id && (
                  <span style={{ fontFamily: "'SF Mono', monospace", fontSize: 10, opacity: 0.7, marginLeft: 4 }}>· {order.razorpay_payment_id.slice(0, 14)}</span>
                )}
              </div>
            )}
            {isBypassed && (
              <div
                data-testid="super-admin-bypass-pill"
                title={`${order?.payment_bypass_by_name || 'Super Admin'}${order?.payment_bypass_at ? ' on ' + toIST(order.payment_bypass_at, 'dd MMM yyyy, HH:mm') : ''}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', color: '#F59E0B', fontSize: 10, fontWeight: 500, marginBottom: 8 }}
              >
                ⚠ Super Admin approved payment
              </div>
            )}
            <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--admin-text-dim)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Building2 style={{ width: 14, height: 14, color: 'var(--admin-text-soft)' }} />
                {onNavigateToCompany && company ? <button onClick={() => onNavigateToCompany(company)} style={{ background: 'none', border: 'none', color: 'var(--admin-green)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }} data-testid="order-company-link">{company?.company_name}</button> : company?.company_name}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <User style={{ width: 14, height: 14, color: 'var(--admin-text-soft)' }} />
                {onNavigateToUser && user ? <button onClick={() => onNavigateToUser(user)} style={{ background: 'none', border: 'none', color: 'var(--admin-green)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit' }} data-testid="order-user-link">{user?.full_name || user?.email}</button> : (user?.full_name || user?.email)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <StatusBadge status={order?.status} />
            {order?.payment_status !== 'paid' && order?.total_amount > 0 && (
              <button
                className="admin-btn admin-btn-sm"
                onClick={() => setShowDiscount(true)}
                data-testid="discount-btn"
                title="Apply discount % to this order"
                style={{ background: 'transparent', border: '1px solid #F59E0B', color: '#F59E0B', fontWeight: 500 }}
              >
                <Percent style={{ width: 14, height: 14 }} />
                {order?.discount_percent > 0 ? `${order.discount_percent}% Off` : 'Discount'}
              </button>
            )}
            {order?.payment_status !== 'paid' && order?.total_amount > 0 && (
              <button
                className="admin-btn admin-btn-sm"
                onClick={handleGeneratePaymentLink}
                disabled={linkLoading}
                data-testid="generate-payment-link-btn"
                title="Generate Razorpay payment link to copy and share"
                style={{
                  background: 'transparent',
                  border: '1px solid #3395FF',
                  color: '#3395FF',
                  fontWeight: 500,
                }}
              >
                {linkLoading
                  ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                  : <CreditCard style={{ width: 14, height: 14 }} />}
                {paymentLink ? 'Payment Link' : 'Generate Payment Link'}
              </button>
            )}
            {order?.asana_task_gid ? (
              <a
                className="admin-btn admin-btn-secondary admin-btn-sm"
                href={`https://app.asana.com/0/1215071868450526/${order.asana_task_gid}/f`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-in-asana-btn"
                title="Open mirrored task in Asana"
                style={{ textDecoration: 'none' }}
              >
                <ExternalLink style={{ width: 14, height: 14 }} /> Open in Asana
              </a>
            ) : isSuperAdmin ? (
              <button
                className="admin-btn admin-btn-sm"
                onClick={handleAddToAsana}
                disabled={syncingAsana}
                data-testid="add-to-asana-btn"
                title="Create the mirror task in Asana"
                style={{
                  background: 'transparent',
                  border: '1px solid #F06A6A',
                  color: '#F06A6A',
                  fontWeight: 500,
                }}
              >
                {syncingAsana
                  ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                  : <Plus style={{ width: 14, height: 14 }} />}
                {syncingAsana ? 'Adding...' : 'Add to Asana'}
              </button>
            ) : null}
            {/* Open / Create Communication (like Asana pattern) */}
            <button
              className="admin-btn admin-btn-sm"
              onClick={async () => {
                try {
                  const res = await api.post(`/tickets/by-order/${order.id}/get-or-create`);
                  const t = res.data;
                  window.location.href = `/admin?tab=tickets&ticket=${t.id}`;
                } catch (e) {
                  const detail = e?.response?.data?.detail;
                  toast.error(detail || 'Could not open communication');
                }
              }}
              data-testid="admin-order-open-communication-btn"
              style={{
                background: 'transparent',
                border: '1px solid #10b981',
                color: '#10b981',
                fontWeight: 500,
              }}
              title="Open (or start) the communication thread for this order"
            >
              <MessageSquare style={{ width: 14, height: 14 }} />
              Open Communication
            </button>
            <button
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={() => setShowClientView(true)}
              data-testid="view-as-client-btn"
              title="See exactly what the client sees for this order"
            >
              <Eye style={{ width: 14, height: 14 }} /> View as Client
            </button>
            {order?.status !== 'completed' && order?.status !== 'cancelled' && (
              <button
                className="admin-btn admin-btn-primary admin-btn-sm"
                onClick={guardedComplete}
                disabled={!canAdvancePastPayment}
                title={!canAdvancePastPayment ? 'Payment not received. Only Super Admin can complete this order.' : 'Mark this order as completed'}
                data-testid="complete-order-btn"
                style={!canAdvancePastPayment ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                <CheckCircle style={{ width: 14, height: 14 }} /> Complete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stepper */}
      <Stepper currentStatus={order?.status} isFiling={isFiling} />

      {/* Deadlines */}
      {(order?.govt_deadline || order?.statutory_deadline || order?.doc_collection_deadline || order?.info_submission_deadline) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }} data-testid="deadline-grid">
          <DeadlineCard label="Info Submission" date={order?.info_submission_deadline} icon={FormInput} />
          <DeadlineCard label="Document Collection" date={order?.doc_collection_deadline} icon={Upload} />
          <DeadlineCard label="Government Due Date" date={order?.govt_deadline || order?.statutory_deadline} icon={AlertTriangle} />
        </div>
      )}

      {/* Main 2-col grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20 }}>
        {/* LEFT COLUMN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Mock Payment */}
          {showMock && (
            <div className="admin-card" style={{ padding: 20 }} data-testid="mock-payment-section">
              <h3 style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <CreditCard style={{ width: 16, height: 16, color: 'var(--admin-text-dim)' }} /> Manual Payment
              </h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 14, background: 'var(--admin-bg)', borderRadius: 8 }}>
                <div><div style={{ fontSize: 13, fontWeight: 600 }}>Record Manual Payment</div><div style={{ fontSize: 12, color: 'var(--admin-text-soft)' }}>Cash, bank transfer, or offline</div></div>
                <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => setShowMockDialog(true)} data-testid="mock-payment-btn"><CreditCard style={{ width: 14, height: 14 }} /> Mock Payment</button>
              </div>
            </div>
          )}

          {/* Invoice Pipeline */}
          {showInvoice && (
            <div className="admin-card" style={{ padding: 20 }} data-testid="invoice-payment-pipeline">
              <h3 style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <CreditCard style={{ width: 16, height: 16, color: 'var(--admin-blue)' }} /> Invoice & Payment Flow
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {[
                  { key: 'invoice_requested', label: '1. Send Invoice', icon: Mail, color: 'blue', action: onSendInvoice, doneLabel: 'Sent', activeLabel: 'Send Invoice' },
                  { key: 'payment_pending', label: '2. Payment', icon: CreditCard, color: 'amber', action: onMarkPaymentReceived, doneLabel: 'Received', activeLabel: 'Mark Received' },
                  { key: 'payment_received', label: '3. Activate Docs', icon: Upload, color: 'green', action: onActivateDocuments, doneLabel: 'Activated', activeLabel: 'Activate' },
                ].map(step => {
                  const isActive = order?.status === step.key;
                  const statusIdx = ['draft','invoice_requested','payment_pending','payment_received'].indexOf(order?.status);
                  const stepIdx = ['draft','invoice_requested','payment_pending','payment_received'].indexOf(step.key);
                  const isDone = statusIdx > stepIdx;
                  return (
                    <div key={step.key} style={{ padding: 14, borderRadius: 8, background: isActive ? `var(--admin-${step.color}-soft)` : 'var(--admin-bg)', border: `1px solid ${isActive ? `var(--admin-${step.color})` : 'var(--admin-border-soft)'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                        <step.icon style={{ width: 14, height: 14, color: `var(--admin-${step.color})` }} />
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{step.label}</span>
                      </div>
                      <button className={`admin-btn admin-btn-sm ${isActive ? 'admin-btn-primary' : 'admin-btn-ghost'}`} disabled={!isActive} onClick={step.action} style={{ width: '100%' }} data-testid={`pipeline-${step.key}`}>
                        {isDone ? step.doneLabel : isActive ? step.activeLabel : 'Pending'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Status Management */}
          <div className="admin-card" style={{ padding: 20 }} data-testid="status-management-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600 }}>Update Status</h3>
              <span style={{ fontSize: 12, color: 'var(--admin-text-soft)' }}>Current: <StatusBadge status={order?.status} /></span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {filteredStatuses.map(s => {
                const isGated = POST_PAYMENT_STATUSES.includes(s) && !canAdvancePastPayment;
                return (
                  <button
                    key={s}
                    className={`admin-btn admin-btn-sm ${order?.status === s ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
                    onClick={() => guardedUpdateStatus(s)}
                    disabled={isGated}
                    title={isGated ? 'Payment not received. Only Super Admin can advance this order past payment.' : ''}
                    data-testid={`status-btn-${s}`}
                    style={isGated ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    {s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </button>
                );
              })}
            </div>
            <div style={{ padding: 14, background: 'var(--admin-green-soft)', border: '1px solid var(--admin-green-border)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><div style={{ fontSize: 13, fontWeight: 700, color: 'var(--admin-green)' }}>Ready to Complete?</div><div style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>Mark as completed once all documents are approved</div></div>
              <button
                className="admin-btn admin-btn-primary admin-btn-sm"
                onClick={guardedComplete}
                disabled={order?.status === 'completed' || !canAdvancePastPayment}
                title={!canAdvancePastPayment ? 'Payment not received. Only Super Admin can complete this order.' : ''}
                data-testid="complete-order-btn-main"
                style={(!canAdvancePastPayment && order?.status !== 'completed') ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                <CheckCircle style={{ width: 14, height: 14 }} /> {order?.status === 'completed' ? 'Completed' : 'Complete'}
              </button>
            </div>
            {order?.status !== 'completed' && order?.status !== 'cancelled' && (
              <div style={{ padding: 12, background: 'var(--admin-red-soft)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--admin-red)' }}>Cancel Order</div>
                <button className="admin-btn admin-btn-danger admin-btn-sm" data-testid="cancel-order-btn" onClick={() => { if (window.prompt('Reason for cancelling (optional):') !== null) onUpdateStatus('cancelled'); }}>
                  <XCircle style={{ width: 14, height: 14 }} /> Cancel
                </button>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="admin-card" style={{ padding: 20 }} data-testid="documents-section">
            <h3 style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <FileText style={{ width: 16, height: 16, color: 'var(--admin-blue)' }} />
              {isFiling ? 'Filing Documents' : 'Document Submissions'} ({submissions?.length || 0})
            </h3>
            {/* KYC */}
            {hasKyc && (
              <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--admin-border)' }}>
                <div className="admin-input-label" style={{ marginBottom: 8 }}>Smart KYC Requirements</div>
                {kycLoading ? <div style={{ textAlign: 'center', padding: 12 }}><Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite', color: 'var(--admin-text-soft)' }} /></div> : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {kycDocs.map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--admin-bg)', borderRadius: 6 }} data-testid={`kyc-item-${item.slug}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {item.status === 'verified' ? <CheckCircle style={{ width: 14, height: 14, color: 'var(--admin-green)' }} /> : <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${item.status === 'rejected' ? 'var(--admin-red)' : 'var(--admin-amber)'}` }} />}
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{item.name}</span>
                        </div>
                        <span className={`admin-status ${item.status === 'verified' ? 'complete' : item.status === 'rejected' ? 'cancelled' : item.status === 'uploaded' ? 'progress' : 'pending'}`} style={{ fontSize: 11 }}>
                          <span className="admin-status-dot" />{item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Requirements */}
            {requirements?.length > 0 && (
              <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--admin-border)' }}>
                <div className="admin-input-label" style={{ marginBottom: 8 }}>Required Information & Documents ({requirements.length})</div>
                {requirements.map(req => {
                  const sub = submissions?.find(s => s.requirement_id === req.id);
                  const cfg = sub ? SUB_CFG[sub.status] : null;
                  return (
                    <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--admin-bg)', borderRadius: 6, marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {sub ? <CheckCircle style={{ width: 14, height: 14, color: 'var(--admin-green)' }} /> : <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--admin-blue)' }} />}
                        <span style={{ fontSize: 13 }}>{req.label}</span>
                      </div>
                      {cfg ? <span className={`admin-status ${cfg.cls}`} style={{ fontSize: 11 }}><span className="admin-status-dot" />{cfg.label}</span> : <span style={{ fontSize: 11, color: 'var(--admin-text-soft)' }}>Pending</span>}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Submissions */}
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {submissions?.length > 0 && <div className="admin-input-label" style={{ marginBottom: 8 }}>Submissions ({submissions.length})</div>}
              {submissions?.map(sub => {
                const req = requirements?.find(r => r.id === sub.requirement_id);
                const cfg = SUB_CFG[sub.status] || { label: sub.status, cls: 'draft' };
                return (
                  <div key={sub.id} style={{ padding: 12, background: 'var(--admin-bg)', borderRadius: 8, marginBottom: 6 }} data-testid={`submission-${sub.id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{req?.label || 'Unknown'}</span>
                      <span className={`admin-status ${cfg.cls}`} style={{ fontSize: 11 }}><span className="admin-status-dot" />{cfg.label}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--admin-text-soft)', marginBottom: 6 }}>{sub.value || 'No value'}</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {sub.file_url && <a href={getFileUrl(sub.file_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--admin-green)', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }} data-testid={`view-submission-${sub.id}`}><Eye style={{ width: 12, height: 12 }} /> View</a>}
                      {sub.status === 'submitted' && <>
                        <button className="admin-btn admin-btn-sm" style={{ background: 'var(--admin-green-soft)', color: 'var(--admin-green)', border: 'none', padding: '3px 8px', fontSize: 11 }} onClick={() => onReviewSubmission(sub.id, 'approved')} data-testid={`approve-${sub.id}`}><Check style={{ width: 12, height: 12 }} /> Approve</button>
                        <button className="admin-btn admin-btn-sm" style={{ background: 'var(--admin-red-soft)', color: 'var(--admin-red)', border: 'none', padding: '3px 8px', fontSize: 11 }} onClick={() => setReviewingSubmission(sub)} data-testid={`reject-${sub.id}`}><XCircle style={{ width: 12, height: 12 }} /> Reject</button>
                      </>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Signature Documents */}
          <div className="admin-card" style={{ padding: 20 }} data-testid="signature-documents-section">
            <h3 style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <FileText style={{ width: 16, height: 16, color: 'var(--admin-amber)' }} />
              {isFiling ? 'Filing Documents for Approval' : 'Signature Documents'} ({signature_documents?.length || 0})
            </h3>
            <div style={{ padding: 14, background: 'var(--admin-bg)', borderRadius: 8, border: '1px dashed var(--admin-amber-border)', marginBottom: 12 }}>
              <input className="admin-input" placeholder={isFiling ? "Caption (e.g. GST Return Draft)" : "Caption (e.g. INC-20A)"} value={signatureCaption} onChange={e => setSignatureCaption(e.target.value)} style={{ marginBottom: 8 }} data-testid="signature-caption-input" />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="file" ref={signatureFileRef} className="hidden" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={e => setSignatureFileName(e.target.files?.[0]?.name || '')} />
                <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => signatureFileRef.current?.click()} data-testid="signature-choose-file-btn"><Upload style={{ width: 14, height: 14 }} /> Choose File</button>
                {signatureFileName && <span style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>{signatureFileName}</span>}
                <button className="admin-btn admin-btn-primary admin-btn-sm" style={{ marginLeft: 'auto', background: 'var(--admin-amber)', color: '#000' }} onClick={onUploadSignatureDocument} disabled={uploadingSignature || !signatureCaption || !signatureFileName} data-testid="upload-signature-btn">
                  {uploadingSignature ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 14, height: 14 }} />} {isFiling ? 'Send' : 'Upload'}
                </button>
              </div>
            </div>
            {signature_documents?.map(doc => {
              const lbl = isFiling ? (doc.status === 'approved' || doc.client_action === 'approve' ? 'Approved' : doc.status === 'changes_requested' || doc.client_action === 'reject' ? 'Rejected' : 'Awaiting') : (doc.status === 'approved' ? 'Approved' : doc.status === 'rejected' ? 'Rejected' : doc.status === 'signed' ? 'Under Review' : 'Pending Signature');
              const cls = lbl === 'Approved' ? 'complete' : lbl === 'Rejected' ? 'cancelled' : lbl === 'Under Review' || lbl === 'Signed' ? 'progress' : 'pending';
              const rejHistory = doc.rejection_history || [];
              // Verify/Reject available for non-filing products when client has uploaded a signed copy that is still under review
              const canReview = !isFiling && !!doc.signed_url && doc.status === 'signed';
              const attemptNum = doc.attempt_count || (doc.signed_url ? 1 : 0);
              return (
                <div key={doc.id} style={{ padding: 12, background: 'var(--admin-bg)', borderRadius: 8, marginBottom: 6 }} data-testid={`signature-doc-${doc.id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {doc.document_name}
                      {attemptNum > 1 && <span style={{ fontSize: 11, color: 'var(--admin-text-soft)', marginLeft: 6 }}>· Attempt {attemptNum}</span>}
                    </span>
                    <span className={`admin-status ${cls}`} style={{ fontSize: 11 }}><span className="admin-status-dot" />{lbl}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {doc.document_url && <a href={getFileUrl(doc.document_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--admin-green)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}><Download style={{ width: 12, height: 12 }} /> Original</a>}
                    {doc.signed_url && <a href={getFileUrl(doc.signed_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--admin-green)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}><Download style={{ width: 12, height: 12 }} /> Signed</a>}
                    {doc.client_remark && <button className="admin-btn admin-btn-ghost admin-btn-sm" style={{ fontSize: 11 }} onClick={() => setRemarkPopup({ show: true, remark: doc.client_remark, docName: doc.document_name })}><Eye style={{ width: 12, height: 12 }} /> Client Remark</button>}
                    {rejHistory.length > 0 && (
                      <button className="admin-btn admin-btn-ghost admin-btn-sm" style={{ fontSize: 11 }} onClick={() => setRejectionHistoryPopup({ show: true, history: rejHistory, docName: doc.document_name })} data-testid={`rejection-history-btn-${doc.id}`}>
                        <FileText style={{ width: 12, height: 12 }} /> History ({rejHistory.length})
                      </button>
                    )}
                    {onDeleteSignatureDocument && doc.status !== 'approved' && (
                      <button
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        style={{ fontSize: 11, color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}
                        onClick={() => setDeleteSigConfirm({ show: true, doc })}
                        data-testid={`delete-signed-doc-${doc.id}`}
                        title="Delete this signature document (e.g., wrong file uploaded)"
                      >
                        <Trash2 style={{ width: 12, height: 12 }} /> Delete
                      </button>
                    )}
                    {canReview && (
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        <button
                          className="admin-btn admin-btn-sm"
                          style={{ background: '#10b981', color: '#fff', fontSize: 11 }}
                          disabled={signedActionLoading[doc.id]}
                          onClick={async () => {
                            setSignedActionLoading(prev => ({ ...prev, [doc.id]: true }));
                            try { await onReviewSignedDocument(doc.id, 'approved'); }
                            finally { setSignedActionLoading(prev => ({ ...prev, [doc.id]: false })); }
                          }}
                          data-testid={`verify-signed-doc-${doc.id}`}
                        >
                          {signedActionLoading[doc.id] ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <CheckCircle style={{ width: 12, height: 12 }} />} Verify
                        </button>
                        <button
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          style={{ fontSize: 11 }}
                          disabled={signedActionLoading[doc.id]}
                          onClick={() => setSignedRejectModal({ show: true, doc, remark: '' })}
                          data-testid={`reject-signed-doc-${doc.id}`}
                        >
                          <X style={{ width: 12, height: 12 }} /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                  {doc.admin_comment && doc.status === 'rejected' && (
                    <div style={{ marginTop: 8, padding: 10, background: 'rgba(239,68,68,0.08)', borderLeft: '3px solid #ef4444', borderRadius: 4, fontSize: 12, color: 'var(--admin-text-dim)' }}>
                      <strong style={{ color: '#ef4444' }}>Rejection remark:</strong> {doc.admin_comment}
                    </div>
                  )}
                </div>
              );
            })}
            {(!signature_documents || !signature_documents.length) && <p style={{ textAlign: 'center', color: 'var(--admin-text-soft)', fontSize: 13, padding: 16 }}>{isFiling ? 'No filing documents' : 'No signature documents'}</p>}
          </div>

          {/* Acknowledgement */}
          <div className="admin-card" style={{ padding: 20 }} data-testid="acknowledgement-section">
            <h3 style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <CheckCircle style={{ width: 16, height: 16, color: 'var(--admin-green)' }} /> Acknowledgement & Certificates
            </h3>
            <div style={{ padding: 14, background: 'var(--admin-bg)', borderRadius: 8, border: '1px dashed var(--admin-green-border)', marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input className="admin-input" style={{ flex: '1 1 180px' }} placeholder="Document name" value={ackDocName} onChange={e => setAckDocName(e.target.value)} data-testid="ack-doc-name-input" />
                <input type="file" ref={ackFileRef} style={{ display: 'none' }} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={e => setAckFileName(e.target.files?.[0]?.name || '')} />
                <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => ackFileRef.current?.click()} data-testid="ack-choose-file-btn"><Upload style={{ width: 14, height: 14 }} /> File</button>
                {ackFileName && <span style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>{ackFileName}</span>}
                <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={onUploadAck} disabled={uploadingAck || !ackDocName} data-testid="upload-ack-btn">
                  {uploadingAck ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Upload style={{ width: 14, height: 14 }} />} Upload
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
              {acknowledgement_documents?.map(doc => (
                <div key={doc.id} style={{ padding: 12, background: 'var(--admin-bg)', borderRadius: 8 }} data-testid={`ack-doc-${doc.id}`}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{doc.document_name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <a href={getFileUrl(doc.document_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--admin-green)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Download style={{ width: 14, height: 14 }} /> Download
                    </a>
                    {onDeleteAcknowledgement && (
                      <button
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        style={{ fontSize: 11, color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}
                        onClick={() => setDeleteAckConfirm({ show: true, doc })}
                        data-testid={`delete-ack-doc-${doc.id}`}
                        title="Delete this acknowledgement / certificate (e.g., wrong file uploaded)"
                      >
                        <Trash2 style={{ width: 12, height: 12 }} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {(!acknowledgement_documents || !acknowledgement_documents.length) && <p style={{ color: 'var(--admin-text-soft)', fontSize: 13, padding: 16 }}>No acknowledgements yet</p>}
            </div>
          </div>

          {/* GST Filing */}
          {(product?.department_type?.toUpperCase() === 'GST' || orderDetail?.order?.service_type === 'gst') && (
            <div className="admin-card" style={{ padding: 20, borderColor: 'var(--admin-purple)', borderWidth: 1 }} data-testid="gst-filing-section">
              <GSTFilingTab order={orderDetail} onRefresh={onRefreshOrder} />
            </div>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* DSC Consent */}
          <div className="admin-card" style={{ padding: 20 }} data-testid="dsc-consent-section">
            <h3 style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Shield style={{ width: 16, height: 16, color: 'var(--admin-purple)' }} /> DSC Consent
              {order?.dsc_consent_status && (
                <span className={`admin-status ${order.dsc_consent_status === 'approved' ? 'complete' : order.dsc_consent_status === 'rejected' ? 'cancelled' : 'pending'}`} style={{ fontSize: 11 }}>
                  <span className="admin-status-dot" />{order.dsc_consent_status}
                </span>
              )}
            </h3>
            {(!order?.dsc_consent_status || order.dsc_consent_status === 'rejected') && !showDscModal && (
              <button className="admin-btn admin-btn-sm" style={{ background: 'var(--admin-purple-soft)', color: 'var(--admin-purple)', border: 'none', marginBottom: 8 }} onClick={() => setShowDscModal(true)} data-testid="send-dsc-consent-btn"><Shield style={{ width: 14, height: 14 }} /> Send Consent Request</button>
            )}
            {showDscModal && (
              <div style={{ padding: 14, background: 'var(--admin-purple-soft)', borderRadius: 8, marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-purple)', marginBottom: 8 }}>Confirm DSC Consent</div>
                <div style={{ fontSize: 12, color: 'var(--admin-text-dim)', marginBottom: 8 }}>Client: {user?.full_name || user?.email}<br/>Order: {product?.name} - {company?.company_name}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="admin-btn admin-btn-sm" style={{ background: 'var(--admin-purple)', color: '#fff', border: 'none' }} onClick={handleSendDsc} disabled={sendingDsc} data-testid="confirm-dsc-consent-btn">{sendingDsc ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 12, height: 12 }} />} Send</button>
                  <button className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => setShowDscModal(false)}>Cancel</button>
                </div>
              </div>
            )}
            {order?.dsc_consent_status === 'pending' && <div style={{ padding: 10, background: 'var(--admin-amber-soft)', borderRadius: 6, fontSize: 12, color: 'var(--admin-amber)', marginBottom: 8 }}><Clock style={{ width: 12, height: 12, display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Waiting for client</div>}
            {order?.dsc_consent_status === 'approved' && <div style={{ padding: 10, background: 'var(--admin-green-soft)', borderRadius: 6, fontSize: 12, color: 'var(--admin-green)', marginBottom: 8 }}><CheckCircle style={{ width: 12, height: 12, display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Approved</div>}
            {dscConsents.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div className="admin-input-label">History</div>
                {dscConsents.map(c => (
                  <div key={c.id} style={{ padding: '6px 10px', background: 'var(--admin-bg)', borderRadius: 6, fontSize: 12 }} data-testid={`dsc-consent-${c.id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--admin-text-dim)' }}>{c.filing_purpose}</span>
                      <span className={`admin-status ${c.status === 'approved' ? 'complete' : c.status === 'rejected' ? 'cancelled' : 'pending'}`} style={{ fontSize: 10, padding: '2px 6px' }}><span className="admin-status-dot" />{c.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Govt Filing — shown on ALL orders unconditionally */}
          <GovtSection order={order} onUpdate={async (s, r, a) => {
            try {
              const fd = new FormData(); fd.append('status', s); if (r) fd.append('remark', r); if (a) fd.append('application_number', a); fd.append('send_email', 'true');
              await adminApi.updateGovtFilingStatus(order.id, fd);
              toast.success('Filing status updated'); if (onRefreshOrder) await onRefreshOrder();
            } catch { toast.error('Failed to update'); }
          }} />

          {/* Notes & Activity */}
          <div className="admin-card" style={{ overflow: 'hidden' }} data-testid="notes-activity-panel">
            <div className="admin-tabs">
              <button className={`admin-tab ${detailTab === 'notes' ? 'active' : ''}`} onClick={() => setDetailTab('notes')} data-testid="tab-internal-notes"><MessageSquare style={{ width: 14, height: 14 }} /> Notes</button>
              <button className={`admin-tab ${detailTab === 'activity' ? 'active' : ''}`} onClick={() => setDetailTab('activity')} data-testid="tab-activity-log"><Activity style={{ width: 14, height: 14 }} /> Activity</button>
            </div>
            <div style={{ padding: 16, maxHeight: 400, overflowY: 'auto' }}>
              {detailTab === 'notes' && (
                <div data-testid="panel-internal-notes">
                  <div style={{ padding: 12, background: 'var(--admin-bg)', borderRadius: 8, marginBottom: 12 }}>
                    <textarea className="admin-input" rows={3} value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Write a note..." style={{ marginBottom: 8, background: 'transparent', border: 'none', resize: 'none' }} data-testid="note-content-input" />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--admin-border)', paddingTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="file" ref={noteFileRef} style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) { setNoteFile(f); setNoteFileName(f.name); } }} />
                        <button onClick={() => noteFileRef.current?.click()} style={{ background: 'none', border: 'none', color: 'var(--admin-text-soft)', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }} data-testid="note-attach-file-btn"><Upload style={{ width: 12, height: 12 }} /> Attach</button>
                        {noteFileName && <span style={{ fontSize: 11, color: 'var(--admin-text-dim)' }}>{noteFileName} <button onClick={() => { setNoteFile(null); setNoteFileName(''); }} style={{ background: 'none', border: 'none', color: 'var(--admin-red)', cursor: 'pointer' }}><X style={{ width: 10, height: 10 }} /></button></span>}
                      </div>
                      <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={handlePostNote} disabled={postingNote || !noteContent.trim()} data-testid="post-note-btn">
                        {postingNote ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : null} Post
                      </button>
                    </div>
                  </div>
                  {loadingNotes ? <div style={{ textAlign: 'center', padding: 20 }}><Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite', color: 'var(--admin-text-soft)' }} /></div> : notes.length === 0 ? <p style={{ textAlign: 'center', color: 'var(--admin-text-soft)', fontSize: 12, padding: 16 }} data-testid="no-notes-msg">No notes yet</p> : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="notes-list">
                      {notes.map(n => (
                        <div key={n.id} style={{ padding: 10, background: 'var(--admin-bg)', borderRadius: 6 }} data-testid="note-card">
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600 }}>{n.posted_by_name}</span>
                            <span style={{ fontSize: 10, color: 'var(--admin-text-soft)' }}>{toIST(n.created_at)}</span>
                          </div>
                          <p style={{ fontSize: 12, color: 'var(--admin-text-dim)', whiteSpace: 'pre-wrap' }}>{n.content}</p>
                          {n.file_url && <a href={getFileUrl(n.file_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--admin-green)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }} data-testid="note-file-link"><Download style={{ width: 12, height: 12 }} /> {n.file_name || 'Download'}</a>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {detailTab === 'activity' && (
                <div data-testid="panel-activity-log">
                  {loadingActivity ? <div style={{ textAlign: 'center', padding: 20 }}><Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite', color: 'var(--admin-text-soft)' }} /></div> : activity.length === 0 ? <p style={{ textAlign: 'center', color: 'var(--admin-text-soft)', fontSize: 12, padding: 16 }} data-testid="no-activity-msg">No activity yet</p> : (
                    <div style={{ position: 'relative' }} data-testid="activity-timeline">
                      <div style={{ position: 'absolute', left: 9, top: 8, bottom: 8, width: 1, background: 'var(--admin-border)' }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {activity.map((e, i) => (
                          <div key={e.id} style={{ display: 'flex', gap: 10, position: 'relative' }} data-testid="activity-entry">
                            <div style={{ width: 20, height: 20, borderRadius: '50%', background: i === 0 ? 'var(--admin-blue)' : 'var(--admin-bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
                              <Clock style={{ width: 10, height: 10, color: i === 0 ? '#fff' : 'var(--admin-text-soft)' }} />
                            </div>
                            <div>
                              <p style={{ fontSize: 12, color: 'var(--admin-text)' }}>{e.description}</p>
                              <span style={{ fontSize: 10, color: 'var(--admin-text-soft)' }}>{toIST(e.created_at)} {e.performed_by_name && `\u00B7 ${e.performed_by_name}`}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── MODALS ────────────────────────────────────── */}
      {showMockDialog && (
        <div className="admin-modal-backdrop" data-testid="mock-payment-dialog">
          <div className="admin-modal">
            <div className="admin-modal-title">Record Mock Payment</div>
            <p style={{ fontSize: 13, color: 'var(--admin-text-dim)', marginBottom: 12 }}>This marks the order as paid without payment gateway.</p>
            <div className="admin-input-label">Remark *</div>
            <textarea className="admin-input" rows={3} value={mockRemark} onChange={e => setMockRemark(e.target.value)} placeholder="e.g. Cash received, bank transfer..." data-testid="mock-payment-remark-input" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="admin-btn admin-btn-ghost" onClick={() => { setShowMockDialog(false); setMockRemark(''); }}>Cancel</button>
              <button className="admin-btn admin-btn-primary" disabled={!mockRemark.trim()} onClick={async () => { await onMockPayment(mockRemark); setMockRemark(''); setShowMockDialog(false); }} data-testid="confirm-mock-payment-btn">Confirm</button>
            </div>
          </div>
        </div>
      )}
      {reviewingSubmission && (
        <div className="admin-modal-backdrop" data-testid="review-modal">
          <div className="admin-modal">
            <div className="admin-modal-title">Reject Document</div>
            <textarea className="admin-input" rows={4} value={reviewComment} onChange={e => setReviewComment(e.target.value)} placeholder="Reason for rejection..." data-testid="review-comment-input" />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="admin-btn admin-btn-ghost" onClick={() => setReviewingSubmission(null)}>Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={() => onReviewSubmission(reviewingSubmission.id, 'rejected')} data-testid="confirm-reject-btn">Reject</button>
            </div>
          </div>
        </div>
      )}
      {remarkPopup.show && (
        <div className="admin-modal-backdrop" onClick={() => setRemarkPopup({ show: false, remark: '', docName: '' })} data-testid="remark-popup">
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="admin-modal-title" style={{ margin: 0 }}>Client Remark</div>
              <button style={{ background: 'none', border: 'none', color: 'var(--admin-text-soft)', cursor: 'pointer' }} onClick={() => setRemarkPopup({ show: false, remark: '', docName: '' })}><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="admin-input-label">Document: {remarkPopup.docName}</div>
            <div style={{ padding: 14, background: 'var(--admin-bg)', borderRadius: 8, color: 'var(--admin-text-dim)', fontSize: 13 }}>{remarkPopup.remark}</div>
          </div>
        </div>
      )}

      {signedRejectModal.show && (
        <div className="admin-modal-backdrop" data-testid="signed-reject-modal">
          <div className="admin-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="admin-modal-title" style={{ margin: 0 }}>Reject Signed Document</div>
              <button style={{ background: 'none', border: 'none', color: 'var(--admin-text-soft)', cursor: 'pointer' }} onClick={() => setSignedRejectModal({ show: false, doc: null, remark: '' })}><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="admin-input-label" style={{ marginBottom: 8 }}>Document: <strong>{signedRejectModal.doc?.document_name}</strong></div>
            <div className="admin-input-label" style={{ marginBottom: 4 }}>Rejection remark <span style={{ color: '#ef4444' }}>*</span> (visible to client)</div>
            <textarea
              className="admin-input"
              rows={4}
              value={signedRejectModal.remark}
              onChange={e => setSignedRejectModal(prev => ({ ...prev, remark: e.target.value }))}
              placeholder="e.g. Signature missing on page 2, document is blurry, wrong name..."
              data-testid="signed-reject-remark-input"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="admin-btn admin-btn-ghost" onClick={() => setSignedRejectModal({ show: false, doc: null, remark: '' })}>Cancel</button>
              <button
                className="admin-btn admin-btn-danger"
                disabled={!signedRejectModal.remark.trim() || signedActionLoading[signedRejectModal.doc?.id]}
                onClick={async () => {
                  const d = signedRejectModal.doc;
                  const remark = signedRejectModal.remark.trim();
                  setSignedActionLoading(prev => ({ ...prev, [d.id]: true }));
                  try {
                    await onReviewSignedDocument(d.id, 'rejected', remark);
                    setSignedRejectModal({ show: false, doc: null, remark: '' });
                  } finally {
                    setSignedActionLoading(prev => ({ ...prev, [d.id]: false }));
                  }
                }}
                data-testid="signed-reject-confirm-btn"
              >
                {signedActionLoading[signedRejectModal.doc?.id] ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <X style={{ width: 14, height: 14 }} />} Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectionHistoryPopup.show && (
        <div className="admin-modal-backdrop" onClick={() => setRejectionHistoryPopup({ show: false, history: [], docName: '' })} data-testid="rejection-history-popup">
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="admin-modal-title" style={{ margin: 0 }}>Rejection History</div>
              <button style={{ background: 'none', border: 'none', color: 'var(--admin-text-soft)', cursor: 'pointer' }} onClick={() => setRejectionHistoryPopup({ show: false, history: [], docName: '' })}><X style={{ width: 18, height: 18 }} /></button>
            </div>
            <div className="admin-input-label" style={{ marginBottom: 12 }}>Document: <strong>{rejectionHistoryPopup.docName}</strong></div>
            <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {rejectionHistoryPopup.history.map((entry, i) => (
                <div key={i} style={{ padding: 12, background: 'rgba(239,68,68,0.06)', borderLeft: '3px solid #ef4444', borderRadius: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 11, color: 'var(--admin-text-soft)' }}>
                    <span>Attempt #{entry.attempt_number || i + 1} · by {entry.admin_name || 'Admin'}</span>
                    <span>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--admin-text)' }}>{entry.remark || '—'}</div>
                </div>
              ))}
              {rejectionHistoryPopup.history.length === 0 && <p style={{ textAlign: 'center', color: 'var(--admin-text-soft)', fontSize: 13, padding: 12 }}>No prior rejections</p>}
            </div>
          </div>
        </div>
      )}

      {deleteSigConfirm.show && (
        <div className="admin-modal-backdrop" data-testid="delete-signed-doc-modal">
          <div className="admin-modal" style={{ maxWidth: 460 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ padding: 8, borderRadius: 8, background: 'rgba(239,68,68,0.15)' }}>
                <AlertTriangle style={{ width: 18, height: 18, color: '#ef4444' }} />
              </div>
              <div className="admin-modal-title" style={{ margin: 0 }}>Delete signature document?</div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--admin-text-dim)', margin: '10px 0 6px' }}>
              You&apos;re about to permanently delete <strong>{deleteSigConfirm.doc?.document_name}</strong>.
            </p>
            <ul style={{ fontSize: 12, color: 'var(--admin-text-soft)', paddingLeft: 18, margin: '4px 0 14px', lineHeight: 1.6 }}>
              <li>The file will be removed from storage.</li>
              <li>The 7-day signature reminder emails will stop for this document.</li>
              {deleteSigConfirm.doc?.signed_url && <li style={{ color: '#f59e0b' }}>Client&apos;s signed copy will also be discarded.</li>}
              <li>This action cannot be undone.</li>
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="admin-btn admin-btn-ghost" onClick={() => setDeleteSigConfirm({ show: false, doc: null })} data-testid="delete-signed-doc-cancel">Cancel</button>
              <button
                className="admin-btn admin-btn-danger"
                disabled={signedActionLoading[deleteSigConfirm.doc?.id]}
                onClick={async () => {
                  const d = deleteSigConfirm.doc;
                  setSignedActionLoading(prev => ({ ...prev, [d.id]: true }));
                  try {
                    await onDeleteSignatureDocument(d.id);
                    setDeleteSigConfirm({ show: false, doc: null });
                  } finally {
                    setSignedActionLoading(prev => ({ ...prev, [d.id]: false }));
                  }
                }}
                data-testid="delete-signed-doc-confirm"
              >
                {signedActionLoading[deleteSigConfirm.doc?.id] ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Trash2 style={{ width: 14, height: 14 }} />} Delete Document
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteAckConfirm.show && (
        <div className="admin-modal-backdrop" data-testid="delete-ack-doc-modal">
          <div className="admin-modal" style={{ maxWidth: 460 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{ padding: 8, borderRadius: 8, background: 'rgba(239,68,68,0.15)' }}>
                <AlertTriangle style={{ width: 18, height: 18, color: '#ef4444' }} />
              </div>
              <div className="admin-modal-title" style={{ margin: 0 }}>Delete acknowledgement / certificate?</div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--admin-text-dim)', margin: '10px 0 6px' }}>
              You&apos;re about to permanently delete <strong>{deleteAckConfirm.doc?.document_name}</strong>.
            </p>
            <ul style={{ fontSize: 12, color: 'var(--admin-text-soft)', paddingLeft: 18, margin: '4px 0 14px', lineHeight: 1.6 }}>
              <li>The file will be removed from storage.</li>
              <li>The client will no longer be able to download it.</li>
              <li>This action cannot be undone.</li>
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="admin-btn admin-btn-ghost" onClick={() => setDeleteAckConfirm({ show: false, doc: null })} data-testid="delete-ack-doc-cancel">Cancel</button>
              <button
                className="admin-btn admin-btn-danger"
                disabled={ackDeleting}
                onClick={async () => {
                  const d = deleteAckConfirm.doc;
                  setAckDeleting(true);
                  try {
                    await onDeleteAcknowledgement(d.id);
                    setDeleteAckConfirm({ show: false, doc: null });
                  } finally {
                    setAckDeleting(false);
                  }
                }}
                data-testid="delete-ack-doc-confirm"
              >
                {ackDeleting ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Trash2 style={{ width: 14, height: 14 }} />} Delete Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View as Client modal (admin-only utility) */}
      {showClientView && (
        <ClientViewModal
          orderId={order?.id}
          productName={product?.name}
          documentRequirements={requirements || []}
          onClose={() => setShowClientView(false)}
          onUploaded={() => { if (onRefreshOrder) onRefreshOrder(); }}
        />
      )}

      {/* Payment Link modal */}
      {showLinkModal && paymentLink && (
        <div onClick={() => setShowLinkModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--admin-card-bg, #0f1611)', border: '1px solid var(--admin-border-soft)', borderRadius: 12, width: '100%', maxWidth: 520 }}
            data-testid="payment-link-modal">
            <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--admin-border-soft)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontFamily: "'Lora', serif", fontSize: 18, fontWeight: 600 }}>Razorpay Payment Link</h3>
              <button onClick={() => setShowLinkModal(false)} style={{ background: 'none', border: 'none', color: 'var(--admin-text-dim)', cursor: 'pointer' }}>
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>
            <div style={{ padding: 22 }}>
              {paymentLink.status === 'paid' ? (
                <div style={{ marginBottom: 14, padding: 12, borderRadius: 10, background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.35)', display: 'flex', alignItems: 'center', gap: 10 }} data-testid="payment-link-paid-banner">
                  <CheckCircle style={{ width: 20, height: 20, color: '#34D399' }} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#34D399' }}>Payment Received</div>
                    <div style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>Order marked as paid via Razorpay Payment Link.</div>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 14 }}>
                  <div className="admin-input-label" style={{ marginBottom: 6 }}>Share this link with the client</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="admin-input"
                      readOnly
                      value={paymentLink.short_url || ''}
                      onFocus={(e) => e.target.select()}
                      style={{ flex: 1, fontFamily: "'SF Mono', monospace", fontSize: 13 }}
                      data-testid="payment-link-url"
                    />
                    <button className="admin-btn admin-btn-primary" onClick={copyPaymentLink} data-testid="copy-payment-link-btn">
                      <FileText style={{ width: 14, height: 14 }} /> Copy
                    </button>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13, color: 'var(--admin-text-dim)', alignItems: 'center' }}>
                <div><b style={{ color: 'var(--admin-text)' }}>Amount:</b> ₹{(paymentLink.amount || 0).toLocaleString('en-IN')}</div>
                <div><b style={{ color: 'var(--admin-text)' }}>Paid:</b> ₹{(paymentLink.amount_paid || 0).toLocaleString('en-IN')}</div>
                <div>
                  <b style={{ color: 'var(--admin-text)' }}>Status:</b>{' '}
                  <span style={{
                    color: paymentLink.status === 'paid' ? '#34D399' : paymentLink.status === 'expired' ? '#F87171' : '#F59E0B',
                    fontWeight: 500, textTransform: 'capitalize'
                  }} data-testid="payment-link-status">{paymentLink.status}</span>
                </div>
                {paymentLink.id && <div><b style={{ color: 'var(--admin-text)' }}>Ref:</b> <span style={{ fontFamily: "'SF Mono', monospace" }}>{paymentLink.id}</span></div>}
                <button
                  className="admin-btn admin-btn-sm"
                  onClick={refreshPaymentStatus}
                  disabled={linkLoading}
                  data-testid="refresh-payment-status-btn"
                  style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid var(--admin-border-soft)' }}
                  title="Fetch latest status from Razorpay"
                >
                  {linkLoading
                    ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />
                    : <RotateCw style={{ width: 12, height: 12 }} />}
                  Refresh
                </button>
              </div>
              {paymentLink.status !== 'paid' && (
                <p style={{ marginTop: 14, fontSize: 12, color: 'var(--admin-text-soft)', lineHeight: 1.5 }}>
                  Client can pay via UPI, card, or net banking — <b>no login required</b>. Once paid, the order will
                  automatically update to <b>Paid</b> — this modal auto-refreshes every 5 seconds.
                </p>
              )}
              {paymentLink.payment_date && paymentLink.status === 'paid' && (
                <p style={{ marginTop: 14, fontSize: 12, color: 'var(--admin-text-dim)' }}>
                  <b style={{ color: 'var(--admin-text)' }}>Paid on:</b> {new Date(paymentLink.payment_date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  {paymentLink.payment_reference && <span style={{ marginLeft: 12 }}><b style={{ color: 'var(--admin-text)' }}>Payment ID:</b> <span style={{ fontFamily: "'SF Mono', monospace" }}>{paymentLink.payment_reference}</span></span>}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Discount modal */}
      {showDiscount && (
        <div onClick={() => setShowDiscount(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--admin-card-bg, #0f1611)', border: '1px solid var(--admin-border-soft)', borderRadius: 12, width: '100%', maxWidth: 440 }}
            data-testid="discount-modal">
            <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--admin-border-soft)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontFamily: "'Lora', serif", fontSize: 18, fontWeight: 600 }}>Apply Discount</h3>
              <button onClick={() => setShowDiscount(false)} style={{ background: 'none', border: 'none', color: 'var(--admin-text-dim)', cursor: 'pointer' }}>
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>
            <div style={{ padding: 22 }}>
              <div className="admin-input-label" style={{ marginBottom: 8 }}>Quick pick</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {[0, 10, 20, 25, 35, 40].map((p) => (
                  <button key={p}
                    onClick={() => setDiscountVal(String(p))}
                    data-testid={`discount-preset-${p}`}
                    className="admin-btn admin-btn-sm"
                    style={{
                      background: String(p) === discountVal ? '#F59E0B' : 'transparent',
                      color: String(p) === discountVal ? '#000' : 'var(--admin-text)',
                      border: '1px solid ' + (String(p) === discountVal ? '#F59E0B' : 'var(--admin-border-soft)'),
                      minWidth: 60,
                    }}>
                    {p === 0 ? 'No Discount' : `${p}%`}
                  </button>
                ))}
              </div>
              <div className="admin-input-label" style={{ marginBottom: 6 }}>Custom (0–100)</div>
              <input type="number" min="0" max="100" step="0.01" value={discountVal}
                onChange={(e) => setDiscountVal(e.target.value)}
                className="admin-input" style={{ width: '100%' }} data-testid="discount-custom-input" />
              <p style={{ marginTop: 12, fontSize: 12, color: 'var(--admin-text-soft)', lineHeight: 1.5 }}>
                Discount is applied to the base order amount. Any existing Razorpay payment link is invalidated so the client sees the fresh amount.
              </p>
              {order?.base_amount > 0 && (
                <div style={{ marginTop: 10, padding: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, fontSize: 13 }}>
                  Base: ₹{order.base_amount.toLocaleString('en-IN')} · After {discountVal || 0}%: <b>₹{(order.base_amount * (1 - (parseFloat(discountVal) || 0) / 100)).toLocaleString('en-IN', {maximumFractionDigits: 2})}</b>
                </div>
              )}
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid var(--admin-border-soft)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="admin-btn" onClick={() => setShowDiscount(false)}>Cancel</button>
              <button className="admin-btn admin-btn-primary" onClick={applyDiscount} disabled={applyingDiscount} data-testid="apply-discount-btn">
                {applyingDiscount ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Percent style={{ width: 14, height: 14 }} />}
                Apply Discount
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailView;
